// The RESIZE tool: trigger on a work-volume wall handle drags that wall to grow
// or shrink the canvas. Owns the trigger-press start AND the resize_wall commit
// (recenter + one undo snapshot for the whole gesture). The undo snapshot
// machinery captures main-owned canvas state (bounds/units/reference), so main
// injects capture/commit closures rather than exposing that state.

import type * as THREE from "three";
import { uiState } from "../ui/state";
import { interaction } from "../interaction/state";
import { startResizeWall } from "../interaction/handlers/resize";
import { type VolumeBounds } from "../scene/work-volume";
import { type ToolRegistry } from "../app/tool-registry";

export interface ResizeToolDeps {
  tools: ToolRegistry;
  gridGroup: THREE.Object3D;
  volumeBounds: VolumeBounds; // live object (mutated by the drag)
  hotHandle: (i: number) => number; // hovered wall-handle index (-1 = none)
  hotDist: (i: number) => number; // grab distance for that handle
  surfaceAt: (idx: number) => { axis: 0 | 1 | 2; outwardSign: -1 | 1 };
  axisDriven: () => boolean; // desktop mouse drives depth along the axis
  // Undo snapshot, one step per gesture: capture at press; at release commit
  // iff the canvas bounds actually changed.
  captureSnapshot: () => unknown;
  commitSnapshotIfChanged: (before: unknown) => void;
  recenterCanvasBounds: () => void; // snap the footprint back onto the origin
}

export function installResizeTool(deps: ResizeToolDeps): void {
  // Captured at the start of a resize gesture; committed as the undo "before"
  // when the gesture changes the canvas. Tool-local — one gesture at a time.
  let snapBefore: unknown = null;

  deps.tools.register({
    id: "resize",
    claims: () => uiState.topMode === "resize",
    start: (i) => {
      const idx = deps.hotHandle(i);
      if (idx < 0) return;
      // Snapshot the model so the whole resize gesture is one undo step
      // (committed on release in the commit below).
      snapBefore = deps.captureSnapshot();
      const surf = deps.surfaceAt(idx);
      startResizeWall(
        surf.axis,
        surf.outwardSign,
        i,
        deps.hotDist(i),
        deps.gridGroup,
        deps.volumeBounds,
        deps.axisDriven(),
      );
    },
    kinds: ["resize_wall"],
    commit: (i) => {
      if (interaction.kind !== "resize_wall" || interaction.controllerIndex !== i) {
        return;
      }
      // Snap the (possibly off-center) footprint back onto the origin.
      deps.recenterCanvasBounds();
      // Commit the resize as one undo step if it actually changed the canvas.
      if (snapBefore !== null) {
        deps.commitSnapshotIfChanged(snapBefore);
        snapBefore = null;
      }
    },
  });
}
